/** DeckOS Gamebook / MUD contracts — game-session authority only. */

export type SceneType =
  | "PASSAGE"
  | "ROOM"
  | "HUB"
  | "CONFLICT"
  | "TERMINAL"
  | "ENDING";

export type MudVerb =
  | "LOOK"
  | "INSPECT"
  | "TALK"
  | "LISTEN"
  | "USE"
  | "TAKE"
  | "QUERY"
  | "MOVE"
  | "JACK_IN"
  | "RETURN"
  | "JACK";

/** Visual CHIPMAP state for gamebook scenes (extends presentation mapping). */
export type GamebookChipVisualState =
  | "CURRENT"
  | "DISCOVERED"
  | "UNKNOWN"
  | "BLOCKED"
  | "DANGER"
  | "INTRUSION"
  | "ENDING";

export type Requirement =
  | { type: "flag"; value: string }
  | { type: "inventory"; value: string }
  | { type: "attribute"; key: string; minimum: number };

export type StateEffect =
  | { type: "flag"; value: string }
  | { type: "discover"; sceneId: string }
  | { type: "lore"; value: string }
  | { type: "inventory"; value: string }
  | { type: "resource"; key: string; delta: number };

export type RuleCheck = {
  attribute: string;
  target: number;
  salt: string;
};

export type ModuleChoice = {
  id: string;
  label: string;
  detail?: string;
  verb?: MudVerb | string;
  targets?: string[];
  consequence?: string;
  requires?: Requirement[];
  effects?: StateEffect[];
  target?: string;
  check?: RuleCheck;
  successTarget?: string;
  failureTarget?: string;
  successEffects?: StateEffect[];
  failureEffects?: StateEffect[];
};

export type ModuleScene = {
  id: string;
  type: SceneType;
  title: string;
  subtitle?: string;
  body: string[];
  /** Percent layout metadata (0–100) for CHIPMAP projection. */
  x: number;
  y: number;
  state?: GamebookChipVisualState | string;
  present?: string[];
  objects?: string[];
  actions: ModuleChoice[];
  ending?: string;
  loreRefs?: string[];
};

export type LoreEntry = {
  title: string;
  body: string;
  classification: "DEVELOPMENT FIXTURE / NON-AUTHORITATIVE";
  moduleSource: string;
};

export type ModuleManifest = {
  id: string;
  title: string;
  version: string;
  startSceneId: string;
  classification: "DEVELOPMENT FIXTURE / NON-AUTHORITATIVE";
  startingState: {
    attributes: Record<string, number>;
    resources: Record<string, number>;
    inventory: string[];
    flags: string[];
    lore: string[];
    discovered: string[];
  };
  scenes: ModuleScene[];
  routes: [string, string][];
  loreEntries: Record<string, Omit<LoreEntry, "classification" | "moduleSource">>;
};

export type TranscriptKind = "SYSTEM" | "PLAYER" | "RULE" | "NARRATION";

export type TranscriptEntry = {
  kind: TranscriptKind;
  text: string;
  at?: string;
};

export type ActionHistoryEntry = {
  index: number;
  sceneId: string;
  actionId: string;
  outcome: string;
  target: string;
  operationId: string;
};

export type CampaignEvent = {
  eventId: string;
  schemaVersion: "1.0.0";
  campaignId: string;
  sessionId: string;
  sequence: number;
  timestamp: string;
  actor: "PLAYER" | "SYSTEM" | "RULE";
  sourceScene: string;
  type: "ACTION_RESOLVED";
  payload: { actionId: string; outcome: string; target: string; state: Record<string, unknown> };
  evidence: { transcriptIndexes: number[]; historyIndex: number };
  visibility: "CAMPAIGN";
  authorityClass: "GAME_SESSION_EVENT";
};

export type ReviewState = "HOLD" | "REJECT" | "CORRECT" | "CAMPAIGN_IMPRINT";
export type CampaignReview = {
  candidateId: string;
  state: ReviewState;
  correction?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  evidence: string[];
};

export const GAME_SAVE_SCHEMA_VERSION = "1.0.0";

export type GameSession = {
  /** Authority class: game-session only — never project/work DELTA or IMPRINT. */
  authorityClass: "GAME_SESSION";
  moduleId: string;
  moduleVersion: string;
  saveSchemaVersion: string;
  sessionId: string;
  seed: string;
  currentSceneId: string;
  selectedSceneId: string;
  attributes: Record<string, number>;
  resources: Record<string, number>;
  inventory: string[];
  flags: string[];
  lore: string[];
  discovered: string[];
  visited: string[];
  transcript: TranscriptEntry[];
  history: ActionHistoryEntry[];
  actionCount: number;
  ending: string | null;
  saveRevision: number;
  lastOperationId?: string;
  updatedAt: string;
  events: CampaignEvent[];
  campaignReviews: CampaignReview[];
};

export type ActionResult =
  | { ok: true; session: GameSession; operationId: string; duplicate: boolean }
  | { ok: false; error: string; session: GameSession };

export type ImportResult =
  | { ok: true; session: GameSession }
  | { ok: false; error: string };
